import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../config";

function StudentInsight({ assessmentId, questionId, netId }) {
  const [insight, setInsight] = useState("");
  const [errorCounts, setErrorCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [conceptSummary, setConceptSummary] = useState(null);
  const [conceptStatus, setConceptStatus] = useState(null);
  const navigate = useNavigate();

  // Function to get the auth header for API calls
  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    async function fetchInsight() {
      try {
        const encodedAssessmentId = encodeURIComponent(assessmentId);
        const encodedQuestionId = encodeURIComponent(questionId);
        const encodedNetId = encodeURIComponent(netId);
        const response = await fetch(
          `${API_URL}/api/insight/${encodedAssessmentId}/${encodedQuestionId}/${encodedNetId}`,
          {
            headers: {
              ...getAuthHeader(),
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          if (response.status === 401) {
            // Handle unauthorized
            localStorage.removeItem("token");
            localStorage.removeItem("user");
            navigate("/");
            return;
          }
          throw new Error("Failed to fetch insight");
        }

        const data = await response.json();
        setInsight(data.LLM_insight);
        setErrorCounts(data.TopicErrorCount);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
    if (assessmentId && questionId && netId) {
      fetchInsight();
    }
  }, [assessmentId, questionId, netId, navigate]);

  useEffect(() => {
    async function fetchConceptInsights() {
      if (!assessmentId || !netId) {
        setConceptSummary(null);
        return;
      }
      try {
        const res = await fetch(
          `${API_URL}/api/insights/progress?assessment_id=${encodeURIComponent(
            assessmentId
          )}`
        );
        if (!res.ok) {
          setConceptStatus(res.status === 404 ? "not_found" : "error");
          setConceptSummary(null);
          return;
        }
        const data = await res.json();
        if (data.status === "success") {
          const entry = data.data?.student_summary?.students?.find(
            (student) => student.net_id === netId
          );
          setConceptSummary(entry || null);
          setConceptStatus(entry ? "ok" : "missing");
        } else {
          setConceptSummary(null);
          setConceptStatus(data.status);
        }
      } catch (err) {
        console.error("Failed to load concept insights", err);
        setConceptSummary(null);
        setConceptStatus("error");
      }
    }
    fetchConceptInsights();
  }, [assessmentId, netId]);

  if (loading) return <p>Loading insight...</p>;

  return (
    <div className="insight-container">
      {/* Temporarily commented out - Your Performance Insight section */}
      {/* 
      <h3>Your Performance Insight</h3>
      {insight ? (
        <div>
          <p>{insight}</p>
          <ul>
            {Object.entries(errorCounts).map(([errorType, count]) => (
              <li key={errorType}>
                {errorType}: {count}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <p>No insight available yet. Keep trying!</p>
      )}
      */}

      <div className="concept-mastery-card">
        <h3>Concept Mastery</h3>
        {conceptSummary ? (
          <>
            <p className="mastery-score">
              Overall mastery: <strong>{conceptSummary.mastery_percentage}%</strong>
            </p>
            {conceptSummary.mastered_concepts?.length > 0 && (
              <div className="mastery-list">
                <h4>Mastered concepts</h4>
                <ul>
                  {conceptSummary.mastered_concepts.map((concept) => (
                    <li key={concept}>{concept}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mastery-list">
              <h4>Focus next</h4>
              {conceptSummary.focus_concepts?.length ? (
                <ul>
                  {conceptSummary.focus_concepts.map((concept) => (
                    <li key={concept}>{concept}</li>
                  ))}
                </ul>
              ) : (
                <p>Great work! You have mastered the tracked concepts.</p>
              )}
            </div>
          </>
        ) : conceptStatus === "not_found" ? (
          <p>
            Concept insights are not available yet. Your instructor may need to
            run progress tracking.
          </p>
        ) : conceptStatus === "error" ? (
          <p>Unable to load mastery data. Please try again later.</p>
        ) : (
          <p>No mastery data recorded for your account.</p>
        )}
      </div>
    </div>
  );
}

export default StudentInsight;
